import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

function StatCard({ label, value, sub, color }) {
  return (
    <div style={{
      background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 12,
      padding: '20px', flex: 1, minWidth: 140
    }}>
      <div style={{ fontSize: 13, color: '#888', marginBottom: 8 }}>{label}</div>
      <div style={{ fontSize: 32, fontWeight: 700, color: color || '#fff' }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

export default function Today({ lastRefresh }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const now      = new Date();
      const tz       = 'Europe/Dublin';
      const todayYmd    = now.toLocaleDateString('en-CA', { timeZone: tz });
      const tomorrowYmd = new Date(now.getTime() + 86400000).toLocaleDateString('en-CA', { timeZone: tz });

      // Today's bookings with student names
      const { data: todayBookings } = await supabase
        .from('bookings')
        .select('booking_id, wa_id, instructor_key, start_iso')
        .eq('status', 'booked')
        .gte('start_iso', `${todayYmd}T00:00:00Z`)
        .lte('start_iso', `${todayYmd}T23:59:59Z`)
        .order('start_iso', { ascending: true });

      // Tomorrow's bookings
      const { count: tomorrowCount } = await supabase
        .from('bookings')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'booked')
        .gte('start_iso', `${tomorrowYmd}T00:00:00Z`)
        .lte('start_iso', `${tomorrowYmd}T23:59:59Z`);

      // Total students
      const { count: totalStudents } = await supabase
        .from('students')
        .select('*', { count: 'exact', head: true });

      // All active instructors with student counts
      const { data: instructors } = await supabase
        .from('instructors')
        .select('instructor_key, instructor_name, instructor_phone')
        .eq('active', true);

      // Student count per instructor
      const instructorData = await Promise.all((instructors || []).map(async inst => {
        const { count: studentCount } = await supabase
          .from('students')
          .select('*', { count: 'exact', head: true })
          .eq('instructor_key', inst.instructor_key);

        const { count: bookingsToday } = await supabase
          .from('bookings')
          .select('*', { count: 'exact', head: true })
          .eq('instructor_key', inst.instructor_key)
          .eq('status', 'booked')
          .gte('start_iso', `${todayYmd}T00:00:00Z`)
          .lte('start_iso', `${todayYmd}T23:59:59Z`);

        const { count: totalBookings } = await supabase
          .from('bookings')
          .select('*', { count: 'exact', head: true })
          .eq('instructor_key', inst.instructor_key)
          .eq('status', 'booked');

        return {
          ...inst,
          studentCount:  studentCount || 0,
          bookingsToday: bookingsToday || 0,
          totalBookings: totalBookings || 0
        };
      }));

      // Load student names for today's schedule
      const waIds = [...new Set((todayBookings || []).map(b => b.wa_id))];
      let nameMap = {};
      if (waIds.length > 0) {
        const { data: students } = await supabase
          .from('students')
          .select('wa_id, name')
          .in('wa_id', waIds);
        for (const s of (students || [])) nameMap[s.wa_id] = s.name;
      }

      setData({
        todayCount:     (todayBookings || []).length,
        tomorrowCount:  tomorrowCount || 0,
        totalStudents:  totalStudents || 0,
        instructorData,
        todayBookings:  (todayBookings || []).map(b => ({
          ...b,
          studentName: nameMap[b.wa_id] || b.wa_id
        }))
      });
      setLoading(false);
    }
    load();
  }, [lastRefresh]);

  if (loading) return <div style={{ color: '#888', padding: 20 }}>Loading...</div>;

  return (
    <div>
      <div style={{ fontSize: 11, color: '#666', marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>
        Today — {new Date().toLocaleDateString('en-GB', {
          timeZone: 'Europe/Dublin', weekday: 'long', day: 'numeric', month: 'long'
        })}
      </div>

      {/* Stat cards */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <StatCard label="Lessons today"    value={data.todayCount}    color="#6c47ff" />
        <StatCard label="Lessons tomorrow" value={data.tomorrowCount} />
        <StatCard label="Total students"   value={data.totalStudents} />
        <StatCard label="Instructors"      value={data.instructorData.length} />
      </div>

      {/* Today's schedule */}
      {data.todayBookings.length > 0 && (
        <div style={{
          background: '#1a1a1a', border: '1px solid #2a2a2a',
          borderRadius: 12, padding: 20, marginBottom: 16
        }}>
          <div style={{ fontSize: 13, color: '#888', marginBottom: 12 }}>Today's schedule</div>
          {data.todayBookings.map(b => {
            const time = new Date(b.start_iso).toLocaleTimeString('en-IE', {
              timeZone: 'Europe/Dublin', hour: '2-digit', minute: '2-digit', hour12: false
            });
            return (
              <div key={b.booking_id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 0', borderBottom: '1px solid #2a2a2a', fontSize: 14
              }}>
                <span style={{ color: '#6c47ff', minWidth: 50, fontWeight: 600 }}>{time}</span>
                <span style={{ flex: 1, paddingLeft: 12 }}>{b.studentName}</span>
                <span style={{ fontSize: 12, color: '#555' }}>{b.instructor_key}</span>
              </div>
            );
          })}
        </div>
      )}

      {data.todayBookings.length === 0 && (
        <div style={{
          background: '#1a1a1a', border: '1px solid #2a2a2a',
          borderRadius: 12, padding: 20, marginBottom: 16,
          color: '#555', fontSize: 14, textAlign: 'center'
        }}>
          No lessons scheduled today
        </div>
      )}

      {/* Instructor panel */}
      <div style={{
        background: '#1a1a1a', border: '1px solid #2a2a2a',
        borderRadius: 12, padding: 20, marginBottom: 16
      }}>
        <div style={{ fontSize: 13, color: '#888', marginBottom: 12 }}>Instructors</div>

        {/* Header row */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 80px 90px 90px',
          padding: '0 0 8px 0', borderBottom: '1px solid #2a2a2a',
          fontSize: 11, color: '#555', textTransform: 'uppercase', letterSpacing: 0.5
        }}>
          <span>Name</span>
          <span style={{ textAlign: 'center' }}>Students</span>
          <span style={{ textAlign: 'center' }}>Today</span>
          <span style={{ textAlign: 'center' }}>All-time</span>
        </div>

        {data.instructorData.map(inst => (
          <div key={inst.instructor_key} style={{
            display: 'grid', gridTemplateColumns: '1fr 80px 90px 90px',
            padding: '10px 0', borderBottom: '1px solid #2a2a2a', alignItems: 'center'
          }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{inst.instructor_name}</div>
              <div style={{ fontSize: 11, color: '#555', marginTop: 2 }}>{inst.instructor_key}</div>
            </div>
            <div style={{ textAlign: 'center', fontSize: 18, fontWeight: 700, color: '#fff' }}>
              {inst.studentCount}
            </div>
            <div style={{ textAlign: 'center', fontSize: 18, fontWeight: 700, color: inst.bookingsToday > 0 ? '#6c47ff' : '#444' }}>
              {inst.bookingsToday}
            </div>
            <div style={{ textAlign: 'center', fontSize: 18, fontWeight: 700, color: '#fff' }}>
              {inst.totalBookings}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}